import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Calendar,
  Clock,
  User,
  Video,
  MapPin,
  Eye,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  RefreshCw,
  Building2,
  MessageSquare,
  Send,
  Plus,
} from 'lucide-react';
import { emrService, EmrAppointment } from '@/services/emrService';
import VisitActionButton from '@/components/visit/VisitActionButton';
import { useClinic } from '@/contexts/ClinicContext';
import { formatToLocalDate } from '@/lib/utils';
import SmartPagination from '../ui/smart-pagination';
import AppointmentWizard from '@/components/emr/AppointmentWizard';
import { useToast } from '@/hooks/use-toast';

interface PatientAppointmentsTabProps {
  patientId: string;
}

// Query key for patient appointments - exported for external invalidation
export const patientAppointmentsQueryKey = (clinicId: string, patientId: string) => 
  ['patient-appointments', clinicId, patientId];

const PatientAppointmentsTab = ({ patientId }: PatientAppointmentsTabProps) => {
  const { clinic, isAdmin, isPractitioner } = useClinic();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('start_time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedAppointment, setSelectedAppointment] = useState<EmrAppointment | null>(null);
  const [isAppointmentSheetOpen, setIsAppointmentSheetOpen] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const isGuidedClinic = clinic?.clinic_archetype === 'guided';
  const canSchedule =
    !isGuidedClinic && (isPractitioner || isAdmin);

  const openNewAppointment = () => {
    setSelectedAppointment(null);
    setIsAppointmentSheetOpen(true);
  };

  const ITEMS_PER_PAGE = 10;

  // TanStack Query for fetching appointments
  const { 
    data: allAppointments = [], 
    isLoading, 
    refetch 
  } = useQuery({
    queryKey: patientAppointmentsQueryKey(clinic?.id || '', patientId),
    queryFn: async () => {
      if (!clinic?.id || !patientId) return [];
      return emrService.getPatientAppointments(clinic.id, patientId);
    },
    enabled: !!clinic?.id && !!patientId,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  // Sort and paginate appointments (memoized)
  const { appointments, totalPages } = useMemo(() => {
    const sorted = [...allAppointments].sort((a, b) => {
      const aVal = new Date(a.start_time).getTime();
      const bVal = new Date(b.start_time).getTime();
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    const startIdx = (page - 1) * ITEMS_PER_PAGE;
    const endIdx = startIdx + ITEMS_PER_PAGE;
    
    return {
      appointments: sorted.slice(startIdx, endIdx),
      totalPages: Math.ceil(sorted.length / ITEMS_PER_PAGE) || 1,
    };
  }, [allAppointments, page, sortField, sortOrder]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className='h-3 w-3 text-slate-400' />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className='h-3 w-3 text-slate-700' />
    ) : (
      <ChevronDown className='h-3 w-3 text-slate-700' />
    );
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleAppointmentClick = (appointment: EmrAppointment) => {
    setSelectedAppointment(appointment);
    setIsAppointmentSheetOpen(true);
  };

  const handleResendLink = async (appointmentId: string) => {
    if (!clinic?.id) return;
    setResendingId(appointmentId);
    try {
      await emrService.resendAppointmentLink(clinic.id, appointmentId);
      toast({
        title: 'Link sent',
        description: 'Appointment link resent to patient successfully',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to resend',
        description: error?.response?.data?.error?.message || 'Could not resend appointment link',
        variant: 'destructive',
      });
    } finally {
      setResendingId(null);
    }
  };

  const canResendLink = (status: string) => {
    return !['cancelled', 'completed', 'no_show'].includes(status);
  };

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return { color: 'bg-blue-100 text-blue-700', label: 'Scheduled' };
      case 'confirmed':
        return { color: 'bg-indigo-100 text-indigo-700', label: 'Confirmed' };
      case 'checked_in':
        return { color: 'bg-purple-100 text-purple-700', label: 'Checked In' };
      case 'in_progress':
        return { color: 'bg-amber-100 text-amber-700', label: 'In Progress' };
      case 'completed':
        return { color: 'bg-emerald-100 text-emerald-700', label: 'Completed' };
      case 'no_show':
        return { color: 'bg-red-100 text-red-700', label: 'No Show' };
      case 'cancelled':
        return { color: 'bg-slate-100 text-slate-600', label: 'Cancelled' };
      case 'rescheduled':
        return { color: 'bg-orange-100 text-orange-700', label: 'Rescheduled' };
      default:
        return { color: 'bg-slate-100 text-slate-600', label: status || 'Unknown' };
    }
  };

  // Map the linked invoice's status into a payment badge. Returns null
  // for appointments with no invoice (clinic opted out of auto-billing
  // or the appointment type was free) so the row stays compact.
  const getPaymentConfig = (invoice: EmrAppointment['invoice']) => {
    if (!invoice) return null;
    switch (invoice.status) {
      case 'paid':
        return { color: 'bg-emerald-100 text-emerald-700', label: 'Paid' };
      case 'partially_paid':
        return { color: 'bg-amber-100 text-amber-700', label: 'Partial' };
      case 'sent':
      case 'open':
        return { color: 'bg-rose-100 text-rose-700', label: 'Awaiting Payment' };
      case 'void':
        return { color: 'bg-slate-100 text-slate-600', label: 'Void' };
      case 'draft':
        return { color: 'bg-slate-100 text-slate-600', label: 'Draft' };
      default:
        return null;
    }
  };

  const formatAppointmentTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const dateStr = formatToLocalDate(startTime, 'MMM DD, YYYY');
    const startStr = start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const endStr = end.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return { date: dateStr, time: `${startStr} - ${endStr}` };
  };

  return (
    <>
      <div className='space-y-6'>
        {/* Main Card */}
        <div className='rounded-lg border border-slate-200 bg-white'>
          {/* Header */}
          <div className='border-b border-slate-100 px-5 py-4'>
            <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-center gap-3'>
                <div
                  className='flex h-9 w-9 items-center justify-center rounded-lg'
                  style={{ backgroundColor: '#4A8A9A' }}
                >
                  <Calendar className='h-4 w-4 text-white' />
                </div>
                <div>
                  <h3 className='text-sm font-semibold text-slate-900'>Appointments</h3>
                  <p className='text-xs text-slate-500'>
                    {isLoading ? 'Loading...' : `Patient appointment history`}
                  </p>
                </div>
              </div>

              <div className='flex flex-wrap items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => refetch()}
                  className='border-slate-200'
                  disabled={isLoading}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {canSchedule && (
                  <Button
                    size='sm'
                    onClick={openNewAppointment}
                    className='bg-[#4A8A9A] hover:bg-[#008f87]'
                  >
                    <Plus className='mr-1.5 h-4 w-4' />
                    New Appointment
                  </Button>
                )}
              </div>
            </div>

            {/* Sort Options */}
            <div className='mt-3 flex items-center gap-2'>
              <span className='text-xs text-slate-500'>Sort by:</span>
              <button
                onClick={() => handleSort('start_time')}
                className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors ${
                  sortField === 'start_time'
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Date
                <SortIcon field='start_time' />
              </button>
              <button
                onClick={() => handleSort('status')}
                className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors ${
                  sortField === 'status'
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Status
                <SortIcon field='status' />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className='p-5'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12'>
                <Loader2 className='h-6 w-6 animate-spin text-[#4A8A9A]' />
                <span className='ml-2 text-sm text-slate-500'>Loading appointments...</span>
              </div>
            ) : appointments.length === 0 ? (
              <div className='py-12 text-center'>
                <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100'>
                  <Calendar className='h-6 w-6 text-slate-400' />
                </div>
                <p className='text-sm text-slate-500'>No appointments found</p>
              </div>
            ) : (
              <div className='space-y-3'>
                {appointments.map(appointment => {
                  const statusConfig = getStatusConfig(appointment.status);
                  const paymentConfig = getPaymentConfig(appointment.invoice);
                  const { date, time } = formatAppointmentTime(
                    appointment.start_time,
                    appointment.end_time
                  );

                  return (
                    <div
                      key={appointment.id}
                      className='rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300'
                    >
                      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                        {/* Appointment Info */}
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-start gap-3'>
                            <div
                              className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg'
                              style={{
                                backgroundColor: appointment.is_telehealth
                                  ? 'rgba(99, 102, 241, 0.12)'
                                  : 'rgba(0, 167, 157, 0.12)',
                              }}
                            >
                              {appointment.visit_mode === 'video' && (
                                <Video className='h-5 w-5' style={{ color: 'rgb(99, 102, 241)' }} />
                              )}
                              {appointment.visit_mode === 'in_person' && (
                                <Building2
                                  className='h-5 w-5'
                                  style={{ color: 'rgb(0, 167, 157)' }}
                                />
                              )}
                              {appointment.visit_mode === 'async_message' && (
                                <MessageSquare
                                  className='h-5 w-5'
                                  style={{ color: 'rgb(168, 85, 247)' }}
                                />
                              )}
                            </div>
                            <div className='min-w-0 flex-1'>
                              <div className='text-sm font-medium text-slate-900'>
                                {appointment.title ||
                                  appointment.serviceType?.name ||
                                  'Appointment'}
                                {appointment.visit_mode === 'video' && (
                                  <Badge className='ml-2 border-0 bg-indigo-50 text-xs text-indigo-600'>
                                    TeleHealth
                                  </Badge>
                                )}
                                {appointment.visit_mode === 'async_message' && (
                                  <Badge className='ml-2 border-0 bg-purple-50 text-xs text-purple-600'>
                                    Async Consult
                                  </Badge>
                                )}
                                {appointment.visit_mode === 'in_person' && (
                                  <Badge className='ml-2 border-0 bg-green-50 text-xs text-green-600'>
                                    In-Person
                                  </Badge>
                                )}
                              </div>
                              <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500'>
                                <span className='flex items-center gap-1'>
                                  <Clock className='h-3 w-3' />
                                  {date} • {time}
                                </span>
                                <span className='flex items-center gap-1'>
                                  <User className='h-3 w-3' />
                                  {appointment.practitioner_name ||
                                    (appointment.practitioner
                                      ? `${appointment.practitioner.first_name} ${appointment.practitioner.last_name}`
                                      : 'Unknown Provider')}
                                </span>
                                {appointment.location?.name && (
                                  <span className='flex items-center gap-1'>
                                    <MapPin className='h-3 w-3' />
                                    {appointment.location.name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Status & Actions */}
                        <div className='flex flex-shrink-0 flex-wrap items-center gap-2'>
                          <Badge
                            className={`${statusConfig.color} rounded-full px-2.5 py-1 text-xs font-medium hover:${statusConfig.color}`}
                          >
                            {statusConfig.label}
                          </Badge>
                          {paymentConfig && (
                            <Badge
                              className={`${paymentConfig.color} rounded-full px-2.5 py-1 text-xs font-medium hover:${paymentConfig.color}`}
                              title={
                                appointment.invoice
                                  ? `Invoice ${appointment.invoice.invoice_number || ''} — $${(appointment.invoice.balance_cents / 100).toFixed(2)} due`
                                  : undefined
                              }
                            >
                              {paymentConfig.label}
                            </Badge>
                          )}
                          {canResendLink(appointment.status) && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResendLink(appointment.id);
                              }}
                              disabled={resendingId === appointment.id}
                              className='h-8 px-3 text-xs'
                              title='Resend appointment link to patient'
                            >
                              {resendingId === appointment.id ? (
                                <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
                              ) : (
                                <Send className='mr-1 h-3.5 w-3.5' />
                              )}
                              Resend
                            </Button>
                          )}
                          <VisitActionButton
                            appointment={appointment}
                            variant='outline'
                            size='sm'
                            className='h-8 px-3 text-xs'
                            stopPropagation
                          />
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => handleAppointmentClick(appointment)}
                            className='h-8 px-3 text-xs'
                          >
                            <Eye className='mr-1 h-3.5 w-3.5' />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className='border-t border-slate-100 px-5 py-4'>
              <SmartPagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Appointment Wizard — handles both new (selectedAppointment === null)
          and edit (existing appointment) flows */}
      {isAppointmentSheetOpen && clinic?.id && (
        <AppointmentWizard
          open={isAppointmentSheetOpen}
          onOpenChange={setIsAppointmentSheetOpen}
          clinicId={clinic.id}
          appointment={selectedAppointment || undefined}
          defaultPatientId={patientId}
          onSave={() => {
            const wasEditing = !!selectedAppointment;
            setIsAppointmentSheetOpen(false);
            setSelectedAppointment(null);
            refetch();
            toast({
              title: 'Success',
              description: wasEditing
                ? 'Appointment updated successfully'
                : 'Appointment scheduled successfully',
              variant: 'success',
            });
          }}
        />
      )}
    </>
  );
};

export default PatientAppointmentsTab;
